/* eslint-disable no-nested-ternary */
import React, { FunctionComponent } from 'react';

import styles from '../Table.module.scss';

export type RowInfo = {
  value: string | React.JSX.Element;
};

export type Row = {
  [key: string]: string | React.ReactNode | RowInfo;
};

export type Column = {
  header: string | React.JSX.Element;
  footer?: string;
  align?: 'left' | 'right' | 'center';
  visiblity?: boolean;
  isSwap?: boolean;
  isSortable?: boolean;
  minWidth?: string;
  onSwap?: () => void;
};

export type Columns = {
  [key: string]: Column | null;
};

export type TableRowProps = {
  variant:
    | 'record'
    | 'pair'
    | 'chart'
    | 'progress'
    | 'overview'
    | 'form'
    | 'plain'
    | 'railz'
    | 'basic'
    | 'detail'
    | 'splitPayment'
    | 'minimal';
  width: string;
  tableId: string;
  row: Row;
  rowIdx: number;
  columns: Columns;
  isExpandable?: boolean;
};

type FC = FunctionComponent<TableRowProps>;

const TableRow: FC = ({
  tableId,
  row,
  rowIdx,
  variant = 'record',
  width = '50%',
  columns,
  isExpandable = false,
}: TableRowProps) => {
  const columnKeys = Object.keys(columns).filter(
    (key) => columns[key] && (columns[key]?.visiblity === undefined || columns[key]?.visiblity === true),
  );

  return isExpandable ? (
    row?.isVisible === 'true' ? (
      <tr className={[styles[row?.class as string], styles[variant]].join(' ')}>
        {columnKeys.map((key, idx) => {
          const cellValue = getCellValue(key, row?.class as string);

          return (
            <td
              key={`${tableId}-cell-${rowIdx}-${idx}`}
              style={{ ...getColumnWidth(width, idx), minWidth: columns[key]?.minWidth }}
              className={[styles[getPositionClass(idx, columns[key]?.align)], styles[getContentClass(cellValue)]].join(
                ' ',
              )}
            >
              {getColumnElement(cellValue)}
            </td>
          );
        })}
      </tr>
    ) : (
      <></>
    )
  ) : (
    <tr className={[styles.row, styles[row.class as string]].join(' ')}>
      {columnKeys.map((key, idx) => {
        const cellValue = getCellValue(key, row.class as string);

        return (
          <td
            key={`${tableId}-cell-${rowIdx}-${idx}`}
            style={{ ...getColumnWidth(width, idx), minWidth: columns[key]?.minWidth }}
            className={[styles[getPositionClass(idx, columns[key]?.align)], styles[getContentClass(cellValue)]].join(
              ' ',
            )}
          >
            {getColumnElement(cellValue)}
          </td>
        );
      })}
    </tr>
  );

  function getCellValue(key: string, classValue?: string): string | React.JSX.Element {
    const cellValue = row[key];

    return getValueFromRow(
      typeof cellValue === 'string' || React.isValidElement(cellValue)
        ? cellValue
        : classValue === 'subRow' || classValue === 'subRow--last'
          ? ''
          : undefined,
    );
  }

  function getValueFromRow(value?: string | React.JSX.Element | RowInfo): string | React.JSX.Element {
    if (value) {
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'object' && 'value' in value) {
        const valueNew = value as any as RowInfo;
        return valueNew.value;
      }

      if (React.isValidElement(value)) {
        return value;
      }
    }

    return '-';
  }

  function getPositionClass(idx: number, align?: 'left' | 'right' | 'center'): string {
    if (align) return align;
    if (variant === 'pair') return 'left';
    return ['left'][idx] || (idx === columnKeys.length - 1 ? 'center' : 'center');
  }

  function getContentClass(value: string | React.JSX.Element): string {
    if (isElement(value)) return '';
    return { '-': 'empty' }[value] || '';
  }

  function getColumnElement(value?: string | React.JSX.Element): React.JSX.Element {
    return isElement(value) ? value : <div>{value}</div>;
  }

  function getColumnWidth(_width: string, idx: number) {
    if (variant !== 'pair') return { width: '' };
    return { width: idx % 2 === 0 ? `${parseInt(_width, 10)}%` : `${100 - parseInt(_width, 10)}%` };
  }

  function isElement(value?: string | React.JSX.Element): value is React.JSX.Element {
    return React.isValidElement(value);
  }
};

export default React.memo(TableRow);
